import { ChevronDown, ChevronUp, Ellipsis } from 'lucide-react';
import * as React from 'react';
import { Translate } from 'react-localize-redux';
import { Cell, HeaderGroup, Row, TableState, useBlockLayout, useExpanded, useResizeColumns, useSortBy, useTable } from 'react-table';
import ReactTooltip from 'react-tooltip';
import { FixedSizeList, ListChildComponentProps, areEqual } from 'react-window';
import styled from 'styled-components';
import { ToolbarImageContainer } from '../../containers_v2/globals/defaultToolbar/style/Style';
import { FlexDiv } from '../../containers_v2/products/style';
import { InfiniteTableToolTip } from '../../containers_v2/reports/interpretor/ReportInterpretor';
import ReportInterpretorContext from '../../containers_v2/reports/interpretor/hoverContext';
import { setLocalStorage } from '../../helpers/localStorage';
import {
	PrimaryColor,
	BorderColor,
	BorderColor2,
	GreenSidely,
	GreySidely,
	GreySidely3,
	PastelPrimaryColor,
	LightGreySidely,
	LightGreySidely2,
	HeaderColor,
	RedSidely,
	SidelyBlack,
	SurfaceColor
} from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import Dropdown from '../dropdown/Dropdown';
import { DropdownData } from '../dropdown/model/Model';
import { Checkbox } from '../filterList/style/Style';
import { TableSortType } from './Table';
import { Arrow, TABLE_HEADER_HEIGHT, TABLE_ROW_HEIGHT } from './style/Style';

const TABLE_FOOTER_HEIGHT = 50;

type FrozenCellProps = { className: string, style?: React.CSSProperties };

function frozenCellProps(column, index: number, pinnedColumns: number): FrozenCellProps {
	if (index >= pinnedColumns) return { className: '' };
	return { className: 'frozen', style: { position: 'sticky', left: column.totalLeft } };
}

function PinColumnsDropdown(props: { pinnedHere: boolean, onChange: (pin: boolean) => void }) {
	return <Dropdown
		stopPropagation
		searchable={false}
		name='PinColumns'
		dropdownStyle={{ optionWidth: '230px', optionLeft: '-210px', height: 'auto' }}
		datalist={[{ value: !props.pinnedHere, label: translateToString(props.pinnedHere ? 'global.unpin_columns' : 'global.pin_columns_until_here') }]}
		JSXButton={() => <ToolbarImageContainer hasPointer rotate='90deg' marginLeft='-3px'><Ellipsis color={GreySidely3} strokeWidth={2.5} /></ToolbarImageContainer>}
		onChange={(value: DropdownData<boolean>) => props.onChange(value.value)}
	/>;
}

type TableChrome = {
	headerGroups: HeaderGroup<any>[],
	footerGroups: HeaderGroup<any>[],
	totalColumnsWidth: number,
	noFooter?: boolean,
	firstColIndex: number,
	setResizeRef: (node: HTMLDivElement | null) => void,
	pinnedColumns: number,
	onPinColumns?: (count: number) => void,
};

const TableChromeContext = React.createContext<TableChrome>(undefined as never);

function CellView(props: { cell: Cell<any, any>, frozen: FrozenCellProps }) {
	const { cell, frozen } = props;
	const context = React.useContext(ReportInterpretorContext);
	const [localTimeout, setLocalTimeout] = React.useState<NodeJS.Timeout>();
	const ref = React.useRef<HTMLElement>();
	const setRef = React.useCallback(node => ref.current = node, []);
	return (
		<div {...cell.getCellProps({ style: frozen.style })} className={`td ${frozen.className}`}
			ref={setRef}
			onMouseEnter={() => {
				localTimeout && clearTimeout(localTimeout);
				const content = cell.column?.toolTip?.(cell.row.original, 'row');
				if (!ref.current || !content) return;
				setLocalTimeout(setTimeout(bounds => context.setHover({
					bounds,
					...content
				}), 200, ref.current.getBoundingClientRect()));
			}}
			onMouseLeave={() => {
				localTimeout && clearTimeout(localTimeout);
				context.setHover(undefined);
			}}
		>
			{cell.render('Cell')}
		</div>
	);
}

type RowData = {
	rows: Row<any>[],
	prepareRow: (row: Row<any>) => void,
	getClassName?: (row: Row<any>, index: number) => string,
	selectedCheckbox?: boolean[],
	pinnedColumns: number,
};

const RenderRow = React.memo(({ index, style, data }: ListChildComponentProps<RowData>) => {
	const { rows, prepareRow, getClassName, selectedCheckbox, pinnedColumns } = data;
	const row = rows[index];
	prepareRow(row);
	let className = 'tr ';
	className += getClassName?.(row, index) ?? '';
	if (row.depth > 0) {
		className += (' gradiant' + row.depth);
	}
	if (row.original.selected || selectedCheckbox?.[row.original.id]) {
		className += ' selected';
	}
	return (
		<div
			{...row.getRowProps({
				style
			})}
			className={className}
		>
			{row.cells.map((cell, index2) => <CellView key={`cell[${index}][${index2}]`} cell={cell} frozen={frozenCellProps(cell.column, index2, pinnedColumns)} />)}
		</div>
	);
}, areEqual);
RenderRow.displayName = 'RenderRow';

export const InfiniteTableStyle = styled.div<{ toolTipNotHidden?: boolean, noFooter?: boolean }>`
    height: 100%;

    .circle {
        filter: drop-shadow(0px 0px 2px color-mix(in oklab, ${SidelyBlack} 35%, transparent));
    }

    .presence {
        text-align: center;
    }

    .table {
        .tr {
            background: ${SurfaceColor};
        }

        .selected {
            background-color: ${PastelPrimaryColor};
        }

        overflow: auto;
        display: block;
        contain: inline-size;
        border-spacing: 0;
        background: ${SurfaceColor};
        height: 100%;
        padding-right: 10px;

        && * {
            font-size: 12px;
        }

        thead, tbody, tfoot {
            display: block;
        }

        .list {
            display: flex;
            flex-direction: column;
            max-width: 100%;

            > * {
                flex-shrink: 0;
            }
        }

        thead {
            position: sticky;
            top: 0;
            z-index: 2;
        }

        tfoot {
            position: sticky;
            bottom: 0;
            z-index: 2;
            margin-top: auto;
        }

        .frozen {
            z-index: 1;
        }

        .td.frozen {
            background: inherit;
        }

        tfoot tr, tfoot .frozen {
            background: ${SurfaceColor};
        }

        .header-content {
            height: 100%;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            font-size: 12px;
            font-weight: 600;
            font-family: "Inter", sans-serif;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            text-align: left;
        }

        .header-no-overflow {
            overflow: visible;
        }

        .has-menu {
            padding-right: 24px;
        }

        .pin-menu {
            position: absolute;
            right: 8px;
            top: 0;
            height: 100%;
            display: flex;
            align-items: center;
        }

        .tr {
            :last-child {
                .td {
                    border-bottom: 0;
                }
            }
        }

        .tr {
            height: ${TABLE_ROW_HEIGHT}px;
        }

        .gradiant1 {
            background: color-mix(in oklab, ${LightGreySidely}, ${SurfaceColor} 50%);
        }

        .gradiant2 {
            background: ${LightGreySidely2}
        }

        .gradiant3 {
            background: ${BorderColor}
        }

        .gradiant4 {
            background: ${BorderColor2}
        }

        .gradiant5 {
            background: ${GreySidely}
        }

        .th {
            margin: 0;
            padding: 0 0 0 10px;
            height: ${TABLE_HEADER_HEIGHT}px;
            background-color: ${HeaderColor};
            border-bottom: solid 0.5px ${BorderColor};
            border-right: solid 0.5px ${BorderColor};

            text-align: left;
            font-style: normal;
            font-weight: 600;
            font-size: 12px;
            line-height: 150%;
            color: ${SidelyBlack};
        }

        .td {
            margin: 0;
            padding: 5px;
            border-bottom: solid 0.5px ${BorderColor};
            border-right: solid 0.5px ${BorderColor};

            text-align: left;
            font-family: "Inter", sans-serif;
            font-style: normal;
            font-weight: 400;
            font-size: 12px;
            line-height: 21px;
            letter-spacing: -0.06em;
            color: ${SidelyBlack};
            .report-text {
                overflow: hidden;
                text-overflow: ellipsis;
                width: 100%;
                white-space: nowrap;
                text-align: left;

            }
            .report-number {
                padding-right: 6px;
            }
        }

        .tamer {
            width: 100%;
        }

        .overflow {
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
        }

        .false-resizer {
            display: inline-block;
            width: 0px;
            height: 100%;
            position: absolute;
            right: 0;
            top: 0;
            transform: translateX(50%);
            z-index: 1;
            ${'' /* prevents from scrolling while dragging on touch devices */}
            touch-action:none;


        }
        .resizer {
       		display: inline-block;
            background: transparent;
            width: 1px;
            height: 100%;
           	${'' /* prevents from scrolling while dragging on touch devices */}
           	touch-action:none;

            &:hover {
            	background-color: ${PrimaryColor};
            }
            &.isResizing {
           		background-color: ${PrimaryColor};
            }
        }

        .resizerWrapper {
        	display: inline-block;
        	${'' /* prevents from scrolling while dragging on touch devices */}
        	touch-action:none;
        	z-index: 1;
        	height: 100%;
        	position: absolute;
        	right: 0;
        	top: 0;
        	transform: translateX(50%);
        	display: inline-block;
         	width: 5px;
        }

        .red-dot {
            height: 12px;
            width: 12px;
            background-color: color-mix(in oklab, ${RedSidely} 80%, transparent);
            border-radius: 50%;
            display: inline-block;
            vertical-align: middle;
        }
        .green-dot {
            height: 12px;
            width: 12px;
            background-color: color-mix(in oklab, ${GreenSidely} 80%, transparent);
            border-radius: 50%;
            display: inline-block;
            vertical-align: middle;
        }

        .percentage {
            display: block;
            text-align: right;
            margin-right: 35%;
        }

        ${p => p.noFooter ? '' : `
        tfoot {
            tr:first-child {
              td {
                border: 1px solid ${BorderColor};
                text-align: center;

                margin: 0;
                padding: 0.70rem;
                border-bottom: 1px solid ${BorderColor};

                text-align: center;

                font-family: "Inter", sans-serif;
                font-style: normal;
                font-weight: 500;
                font-size: 12px;
                line-height: 21px;
                color: ${SidelyBlack};

                :last-child {
                    border-right: 1px solid ${BorderColor};
                }

              }
              height: ${TABLE_FOOTER_HEIGHT}px;
            }
            font-weight: 500;
        }
        `}

        .tooltip2 {
            position: relative;
            display: inline-block;
        }
        .tooltip2 .tooltiptext2 {
            ${p => p.toolTipNotHidden ? '' : 'visibility: hidden;'}
            font-family: "Inter", sans-serif;
            font-style: normal;
            font-weight: 300;
            font-size: 12px;
            line-height: 21px;
            min-width: 120px;
            width: fit-content;
            background-color: ${SurfaceColor};
            color: ${SidelyBlack};
            border-radius: 6px;
            padding: 5px;
            position: absolute;
            z-index: 1;
            transform: translateY(-50%);
            top: 50%;
            right: calc(100% + 20px);

            // border: solid 0.3px rgb(177, 177, 177);
            box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
            -moz-box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
            -webkit-box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
            -o-box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
        }
        .tooltip2:hover .tooltiptext2 {
            visibility: visible;
            opacity: 1;
        }

        ul {
            list-style: none;
        }

        .tooltip3 {
            position: relative;
            display: inline-block;
        }
        .tooltip3 .tooltiptext3 {
            ${p => p.toolTipNotHidden ? '' : 'visibility: hidden;'}
            font-family: "Inter", sans-serif;
            font-style: normal;
            font-weight: 300;
            font-size: 12px;
            line-height: 21px;
            width: 200px;
            background-color: ${SurfaceColor};
            color: ${SidelyBlack};
            border-radius: 6px;
            padding: 5px;
            position: absolute;
            z-index: 1;
            top: -8px;
            right: 110%;

            // border: solid 0.3px rgb(177, 177, 177);
            box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
            -moz-box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
            -webkit-box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
            -o-box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
        }
        .tooltip3:hover .tooltiptext3 {
            visibility: visible;
            opacity: 1;
        }

        .tooltipup {
            position: relative;
            display: inline-block;
        }
        .tooltipup .tooltiptextup {
            ${p => p.toolTipNotHidden ? '' : 'visibility: hidden;'}
            font-family: "Inter", sans-serif;
            font-style: normal;
            font-weight: 300;
            font-size: 12px;
            line-height: 21px;
            width: fit-content;
            min-width: 200px;
            background-color: ${SurfaceColor};
            color: ${SidelyBlack};
            border-radius: 6px;
            padding: 5px;
            position: absolute;
            z-index: 1;
            bottom: 150%;
            left: 50%;
            transform: translateX(-50%);

            // border: solid 0.3px rgb(177, 177, 177);
            box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
            -moz-box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
            -webkit-box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
            -o-box-shadow: 0px 1px 3px color-mix(in oklab, ${SidelyBlack} 25%, transparent);
        }
        .tooltipup:hover .tooltiptextup {
            visibility: visible;
            opacity: 1;
        }
    }
`;

const ListInner = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function ListInner({ style, ...rest }, ref) {
	const { totalColumnsWidth } = React.useContext(TableChromeContext);
	return <div ref={ref} {...rest} style={{ ...style, width: totalColumnsWidth, position: 'relative' }} />;
});

const ListOuter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function ListOuter({ children, ...rest }, ref) {
	const { headerGroups, footerGroups, noFooter, firstColIndex, setResizeRef, pinnedColumns, onPinColumns } = React.useContext(TableChromeContext);
	return <div ref={ref} {...rest}>
		<thead>
			{headerGroups.map(headerGroup => (
				// eslint-disable-next-line react/jsx-key
				<tr {...headerGroup.getHeaderGroupProps()}>
					{headerGroup.headers.map((column: any, i) => {
						let resizeProps;
						let isResizing;
						if (column.oldResize) {
							resizeProps = headerGroup.headers[i - 1]?.getResizerProps();
							isResizing = headerGroup.headers[i - 1]?.isResizing;
						} else {
							resizeProps = column.getResizerProps();
							isResizing = column.isResizing;
						}
						const frozen = frozenCellProps(column, i, pinnedColumns);
						const pinMenu = onPinColumns && !column.noPinOption;
						return (
							// eslint-disable-next-line react/jsx-key
							<div ref={i == firstColIndex ? setResizeRef : null} {...column.getHeaderProps({ style: frozen.style })} className={`th ${frozen.className}`}>
								<div
									{...column.getSortByToggleProps({ title: undefined })}
									className={`header-content ${column.noHeaderEllipsis ? 'header-no-overflow' : ''} ${pinMenu ? 'has-menu' : ''}`}
								>
									{column.render('Header')}
									{column.isSorted
										?
										<FlexDiv flow='column' align='stretch'>
											<Arrow up size='12px' isSorted={column.isSorted}><ChevronUp size={11} color={!column.isSortedDesc ? PrimaryColor : SidelyBlack} /></Arrow>
											<Arrow size='12px' isSorted={column.isSorted}><ChevronDown size={11} color={column.isSortedDesc ? PrimaryColor : SidelyBlack} /></Arrow>
										</FlexDiv>
										: ''}

								</div>
								{pinMenu && <span className='pin-menu'><PinColumnsDropdown pinnedHere={pinnedColumns === i + 1} onChange={pin => onPinColumns(pin ? i + 1 : 0)} /></span>}
								{!column.noResizer ?
									<span className='resizerWrapper' {...resizeProps}>
										<span className={`resizer ${isResizing ? 'isResizing' : ''}`}>
										</span>
									</span> :
									<span className='false-resizer' />}
							</div>
						);
					})}
				</tr>
			))}
		</thead>
		{children}
		{noFooter ? '' :
			<tfoot>
				{footerGroups.map(group => {
					const { key, ...footerGroupProps } = group.getFooterGroupProps();
					return (
						<tr key={key} {...footerGroupProps}>
							{group.headers.map((column, columnIndex) => <Footer key={columnIndex} column={column} frozen={frozenCellProps(column, columnIndex, pinnedColumns)} />)}
						</tr>
					);
				})}
			</tfoot>
		}
	</div>;
});

function RowCheckbox(props: { checked: boolean, setChecked: (id: unknown) => boolean, id: unknown, row }) {
	const [checked, setChecked] = React.useState<boolean>(props.checked);

	return <Checkbox
		isActive={checked}
		onClick={() => {
			const res = props.setChecked(props.id);
			props.row.selected = res;
			setChecked(res);
		}}
		// the box is fourteen pixels in a forty pixel column: `top` with a
		// translate only ever centred it up and down, leaving it against the
		// left edge. `auto` margins centre a block of a known width across
		// whatever the column turns out to be
		style={{ position: 'relative', top: '50%', translate: '0 -50%', margin: '0 auto' }}
	/>;

}

export function InfiniteTable(props: { height: number, columns: any[], data: any[], hidden?: boolean, selectedCheckbox?: boolean[], header_thing?: JSX.Element, noFooter?: boolean, setSelectedCheckbox?: (id: number) => boolean, onLoad?: () => void, onSort?: (sort: TableSortType | undefined) => void, trackFirstColWidth?: boolean, firstColIndex?: number, getClassName?: (row: Row<any>, index: number) => string, initialState?: Partial<TableState<any>>, autoResetExpanded?: boolean, getRowId?: (row: any, relativeIndex: number, parent?: Row<any>) => string, pinnedColumns?: number, onPinColumns?: (count: number) => void }) {
	// Use the state and functions returned from useTable to build your UI
	const { height, columns, data, hidden, selectedCheckbox, setSelectedCheckbox } = props;
	const context = React.useContext(ReportInterpretorContext);
	const resizeRef = React.useRef<HTMLDivElement>();
	const setResizeRef = React.useCallback(node => resizeRef.current = node, []);
	const [firstColWidth, setFirstColWidth] = React.useState<number>();

	React.useEffect(() => {
		props.onLoad?.();
	}, [props.columns, props.data]);

	React.useEffect(() => {
		if (!props.trackFirstColWidth || !resizeRef.current) return;

		let resizeTimeout: NodeJS.Timeout;
		const handleResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				setFirstColWidth(resizeRef.current?.offsetWidth);
			}, 500);
		};

		handleResize();

		const observer = new ResizeObserver(handleResize);
		observer.observe(resizeRef.current);
		return () => {
			clearTimeout(resizeTimeout);
			observer.disconnect();
		};
	}, [resizeRef.current]);

	React.useEffect(() => {
		if (!props.trackFirstColWidth || !firstColWidth) return;
		setLocalStorage('infinite_table_resize', { 'name': firstColWidth });
	}, [firstColWidth]);

	const scrollbarWidth = () => {
		const scrollDiv = document.createElement('div');
		scrollDiv.setAttribute('style', 'width: 100px; height: 100px; overflow: auto; position:absolute; top:-9999px;');
		document.body.appendChild(scrollDiv);
		const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
		// document.body.removeChild(scrollDiv)
		return scrollbarWidth;
	};

	const scrollBarSize = React.useMemo(() => scrollbarWidth(), []);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		footerGroups,
		rows,
		totalColumnsWidth,
		prepareRow,
		state: { sortBy, columnResizing },
		toggleSortBy
	} = useTable(
		{
			columns,
			data,
			// lazy reports keep expansion across subtree splices: they key rows
			// by their stable report row id instead of the index path
			autoResetExpanded: props.autoResetExpanded ?? true,
			autoResetSortBy: false,
			initialState: props.initialState ?? undefined,
			...(props.getRowId ? { getRowId: props.getRowId } : {}),
		},
		useSortBy,
		useResizeColumns,
		useBlockLayout,
		useExpanded,
		hooks => {
			if (setSelectedCheckbox && selectedCheckbox)
				hooks.visibleColumns.push(columns => [
					{
						id: 'selection',
						Header: props.header_thing ?? <></>,
						Cell: ({ row }) => <RowCheckbox row={row.original} id={row.original.id} checked={selectedCheckbox?.[row.original.id] ?? false} setChecked={setSelectedCheckbox} />,
						width: 40,
						noPinOption: true,
					},
					...columns,
				]);
		}
	);
	React.useEffect(() => {
		if (sortBy === undefined || sortBy.length === 0)
			return;
		if (headerGroups[0].headers.some(h => h.id === sortBy[0].id)) {
			toggleSortBy?.(sortBy[0].id, sortBy[0].desc);
		}
	}, [context.evolutionState]);

	React.useEffect(() => {
		if (sortBy.length > 0) {
			props.onSort?.(sortBy[0]);
		}
		else {
			props.onSort?.(undefined);
		}

	}, [sortBy]);

	// columnResizing and totalColumnsWidth are deps so that resizing a column
	// produces a new itemData object: RenderRow is memoized (areEqual) and the
	// cell widths are baked in at render time, so without this the virtualized
	// rows keep their stale widths until a hover or scroll re-renders them.
	const rowData = React.useMemo<RowData>(() => ({
		rows,
		prepareRow,
		getClassName: props.getClassName,
		selectedCheckbox,
		pinnedColumns: props.pinnedColumns ?? 0,
	}), [rows, prepareRow, props.getClassName, selectedCheckbox, columnResizing, totalColumnsWidth, props.pinnedColumns]);
	const chrome: TableChrome = {
		headerGroups,
		footerGroups,
		totalColumnsWidth,
		noFooter: props.noFooter,
		firstColIndex: props.firstColIndex ?? 0,
		setResizeRef,
		pinnedColumns: props.pinnedColumns ?? 0,
		onPinColumns: props.onPinColumns,
	};
	// Render the UI for your table
	return <>
		<div {...getTableProps()} className="table" style={{ height: '100%', visibility: hidden ? 'hidden' : undefined }}>
			<tbody {...getTableBodyProps()}>
				<ReactTooltip id="reason-tooltip" className='menu-tooltip'
					place="top" effect="solid" delayShow={400} type="light"
					arrowColor={PrimaryColor} textColor='white'
					getContent={(key) => {
						if (key == null) {
							return;
						}
						return (<span>
							<Translate id={key} />
						</span>);
					}} />
				<TableChromeContext.Provider value={chrome}>
					<FixedSizeList
						className='list'
						height={height + TABLE_HEADER_HEIGHT + (props.noFooter ? 0 : TABLE_FOOTER_HEIGHT)}
						itemCount={rows.length}
						itemSize={TABLE_ROW_HEIGHT}
						width={totalColumnsWidth + scrollBarSize}
						itemData={rowData}
						outerElementType={ListOuter}
						innerElementType={ListInner}
					>
						{RenderRow}
					</FixedSizeList>
				</TableChromeContext.Provider>
			</tbody>
		</div>
		<InfiniteTableToolTip />
	</>;
}

function Footer(props: { column: HeaderGroup<any>, frozen: FrozenCellProps }) {
	const context = React.useContext(ReportInterpretorContext);
	const [localTimeout, setLocalTimeout] = React.useState<NodeJS.Timeout>();
	const ref = React.useRef<HTMLElement>();
	const setRef = React.useCallback(node => ref.current = node, []);
	return <td {...props.column.getFooterProps({ style: props.frozen.style })} className={props.frozen.className}
		ref={setRef}
		onMouseEnter={() => {
			localTimeout && clearTimeout(localTimeout);
			const content = props.column?.toolTip?.({}, 'footer');
			if (!ref.current || !content) return;
			setLocalTimeout(setTimeout(bounds => context.setHover({
				bounds,
				...content
			}), 200, ref.current.getBoundingClientRect()));
		}}
		onMouseLeave={() => {
			localTimeout && clearTimeout(localTimeout);
			context.setHover(undefined);
		}}
	>{props.column.render('Footer')}</td>;

}
